import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    Renderer2,
    ViewChild,
    Inject,
    Injectable,
    OnInit,
} from '@angular/core';
import { ActivationEnd, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, Observable, Subject, Subscription, takeUntil, withLatestFrom } from 'rxjs';
import { MenuService } from './app.menu.service';
import { AppTopbarComponent } from './app.topbar.component';
import { LayoutService } from './service/app.layout.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { FeatureService } from '@services/features/feature.service';
import { Select, Store } from '@ngxs/store';
import { UserAction, UserState } from '@state/user.state';
import { AuthenticationService } from 'app/auth/service';
import { ConfigService } from 'app/app-config.service';
import { DCAR_APP_CONFIG } from 'app/app-config.service';
import { DCarbonizeAppConfig } from 'app/@types/shared/app-config';
import { LoaderService } from '@services/loader/loader.service';
import { OrganizationAction, OrganizationState } from '@state/organization.state';
import { LocalStorageKeys, StorageService } from '@services/storage/storage.service';
import { AdminDataSyncService } from '@services/users/admin-data-sync.service';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';

@Component({
    selector: 'app-layout',
    templateUrl: './app.layout.component.html',
    styleUrls: ['./app.layout.component.scss'],
})
export class AppLayoutComponent implements OnDestroy, AfterViewChecked {
    overlayMenuOpenSubscription: Subscription;
    menuOutsideClickListener: any;
    menuScrollListener: any;
    @Select(OrganizationState.getOrganization) organization$: Observable<OrganizationDto>;
    organization: OrganizationDto | undefined = undefined;
    public sudoOrganizationName: string;
    public backgroundUrl: string;

    private ngUnsubscribe = new Subject<void>();

    @ViewChild(AppTopbarComponent) appTopbar!: AppTopbarComponent;

    constructor(
        private menuService: MenuService,
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private translateService: CustomTranslateService,
        public featureService: FeatureService,
        private store: Store,
        private cdr: ChangeDetectorRef,
        private authenticationService: AuthenticationService,
        public configService: ConfigService,
        @Inject(DCAR_APP_CONFIG)
        public config: DCarbonizeAppConfig,
        private loader: LoaderService,
        private localStorageService: StorageService,
        private adminSignals: AdminDataSyncService,
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.el.nativeElement.contains(event.target) ||
                        this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }
            if (
                (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
                !this.menuScrollListener
            ) {
                this.menuScrollListener = this.renderer.listen(
                    this.appTopbar.appSidebar.menuContainer.nativeElement,
                    'scroll',
                    (event) => {
                        if (this.layoutService.isDesktop()) {
                            this.hideMenu();
                        }
                    },
                );
            }
            if (this.layoutService.state.staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events
            .pipe(
                filter((e) => e instanceof ActivationEnd),
                takeUntil(this.ngUnsubscribe),
                withLatestFrom(this.organization$),
            )
            .subscribe(([event, org]) => {
                this.organization = org;
                this.hideMenu();
            });
    }
    ngAfterViewChecked(): void {
        if (this.featureService.hasSudoFeature()) {
            this.sudoOrganizationName =
                this.store.selectSnapshot(UserState.getCurrentUserClaims)?.organization?.name ?? '';
            this.cdr.detectChanges();
        }
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(
                new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
                ' ',
            );
        }
    }

    hideMenu() {
        this.layoutService.state.overlayMenuActive = false;
        this.layoutService.state.staticMenuMobileActive = false;
        this.layoutService.state.menuHoverActive = false;
        this.menuService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }
        this.unblockBodyScroll();
    }

    get containerClass() {
        return {
            'layout-light': this.layoutService.config.colorScheme === 'light',
            'layout-dark': this.layoutService.config.colorScheme === 'dark',
            'layout-overlay': this.layoutService.config.menuMode === 'overlay',
            'layout-static': this.layoutService.config.menuMode === 'static',
            'layout-slim': this.layoutService.config.menuMode === 'slim',
            'layout-slim-plus': this.layoutService.config.menuMode === 'slim-plus',
            'layout-horizontal': this.layoutService.config.menuMode === 'horizontal',
            'layout-reveal': this.layoutService.config.menuMode === 'reveal',
            'layout-drawer': this.layoutService.config.menuMode === 'drawer',
            'layout-static-inactive':
                this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config.menuMode === 'static',
            'layout-overlay-active': this.layoutService.state.overlayMenuActive,
            'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
            'p-ripple-disabled': !this.layoutService.config.ripple,
            'layout-sidebar-active': this.layoutService.state.sidebarActive,
            'layout-sidebar-anchored': this.layoutService.state.anchored,
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    logout() {
        this.adminSignals.resetSelection();
        this.adminSignals.currentSelectedOrgId.next(undefined);
        this.store.dispatch(new UserAction.Update());
        this.store.dispatch(new OrganizationAction.Deactivate());
        this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
        this.router.navigateByUrl(`/dedicated_admin/home`);
    }
}
